import React, { useState } from 'react';
import { CreditCard, ShieldCheck, Lock, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../../utils/api';
import { useTalentAuth } from '../../contexts/TalentAuthContext';

interface IPGCheckoutProps {
  amount: number; // In dollars (e.g. 10 or 25)
  tierName: string;
  planId?: number;
  currency?: string;
  onCancel: () => void;
}

const IPGCheckout: React.FC<IPGCheckoutProps> = ({
  amount,
  tierName,
  planId,
  currency = 'USD',
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useTalentAuth();

  const handleStartPayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/membership/create-ipg-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planId,
          tierName,
          amount
        })
      });

      const data = await response.json();

      if (!data.success || !data.actionUrl || !data.params) {
        throw new Error(data.message || 'Failed to initialize payment gateway session');
      }

      // Dynamically create and submit the form to Fiserv Hosted Payment Page (HPP)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.actionUrl;
      form.style.display = 'none';

      Object.entries(data.params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err: any) {
      console.error('IPG Checkout error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Plan Summary Card */}
      <div 
        style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Selected Plan
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>
            {tierName}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '22px', fontWeight: 800, color: '#0284c7' }}>
            ${amount.toFixed(2)}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            {currency.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Security & Accepted Cards Banner */}
      <div 
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '16px 20px',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ShieldCheck size={20} color="#10b981" />
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
            Secure Hosted Payment Page (Fiserv IPG)
          </span>
        </div>

        <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
          You will be redirected to the First Data / Fiserv secure checkout to complete your transaction with 256-bit bank-grade encryption.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '6px', borderTop: '1px dashed #e2e8f0' }}>
          <CreditCard size={16} color="#64748b" />
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            Accepts <strong>Visa</strong>, <strong>MasterCard</strong>, <strong>American Express</strong>
          </span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div 
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            flex: 1,
            padding: '12px 20px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            backgroundColor: '#ffffff',
            color: '#475569',
            fontWeight: 600,
            fontSize: '14px',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleStartPayment}
          disabled={loading}
          style={{
            flex: 2,
            padding: '12px 20px',
            borderRadius: '8px',
            border: 'none',
            background: 'linear-gradient(135deg, #185FA5 0%, #0d386b 100%)',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '14px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(24, 95, 165, 0.3)',
            transition: 'all 0.2s'
          }}
        >
          {loading ? (
            <>
              <Loader2 size={18} className="spin-animation" />
              <span>Redirecting to Fiserv...</span>
            </>
          ) : (
            <>
              <Lock size={16} />
              <span>Pay with Card</span>
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>

      <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
        <Lock size={12} /> Encrypted & Certified by Fiserv First Data
      </div>
    </div>
  );
};

export default IPGCheckout;
